import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { SiteCustomizationActionAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import Modal from "~/components/ui/modal/Modal";
import Spinner from "~/components/ui/spinner/Spinner";
import TogglePill from "~/components/ui/togglePill/TogglePill";
import { buildSearchConditions } from "~/helpers/buildPageSearchConditions";
import { messageFromError } from "~/helpers/messageFromError";
import { parsePaginationHeaders } from "~/helpers/paginationHeaders";
import { tryCatch } from "~/helpers/tryCatch";
import { useStagedBoolean } from "~/hooks/useStagedBoolean";

import type { ConditionGroup } from "./AdvancedConditionsModal";
import styles from "./GenerateCustomizationsModal.module.scss";

type Variant = SiteCustomizationActionAttributes["variants"][number];

type FormValues = {
  variants: Variant[];
};

const VARIANT_OPTIONS: { value: Variant; label: string }[] = [
  { value: "title", label: "Title" },
  { value: "description", label: "Description" },
  { value: "schema", label: "Schema" },
];

// SiteCustomizationAction names the ai.md variant "summary", while the pages
// ransack filter names that same customization "ai_instructions". Map between
// the two so the candidate-count query type-checks.
type CustomizationKindFilter = NonNullable<
  NonNullable<
    NonNullable<Parameters<typeof apiClient.sites.pagesDetail>[1]>["q"]
  >["without_any_customization_kind"]
>[number];

const variantToCustomizationKind = (
  variant: Variant,
): CustomizationKindFilter =>
  variant === "summary" ? "ai_instructions" : variant;

const FORM_ID = "generate-customizations-form";

type GenerateCustomizationsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  siteId: number;
  selectedStatuses: string[];
  selectedPageTypes: string[];
  selectedCustomizations: string[];
  searchValue: string;
  advancedConditions?: ConditionGroup[];
  selectedIds?: number[];
};

export const GenerateCustomizationsModal = ({
  isOpen,
  onClose,
  siteId,
  selectedStatuses,
  selectedPageTypes,
  selectedCustomizations,
  searchValue,
  advancedConditions,
  selectedIds,
}: GenerateCustomizationsModalProps) => {
  const [formKey, setFormKey] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormKey((k) => k + 1);
      setIsFormValid(false);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      modalLabel={
        <span className={styles.modalTitle}>Generate AI Content</span>
      }
      buttonLabel="Generate"
      formId={FORM_ID}
      size="md"
      isDisabled={!isFormValid}
    >
      {isOpen && (
        <GenerateModalContent
          key={formKey}
          siteId={siteId}
          onClose={onClose}
          onValidityChange={setIsFormValid}
          selectedStatuses={selectedStatuses}
          selectedPageTypes={selectedPageTypes}
          selectedCustomizations={selectedCustomizations}
          searchValue={searchValue}
          advancedConditions={advancedConditions}
          selectedIds={selectedIds}
        />
      )}
    </Modal>
  );
};

const GenerateModalContent = ({
  siteId,
  onClose,
  onValidityChange,
  selectedStatuses,
  selectedPageTypes,
  selectedCustomizations,
  searchValue,
  advancedConditions,
  selectedIds,
}: Omit<GenerateCustomizationsModalProps, "isOpen"> & {
  onValidityChange: (valid: boolean) => void;
}) => {
  const queryClient = useQueryClient();

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: { variants: [] },
  });

  const watchedVariants = watch("variants");

  const hasSelection = !!selectedIds && selectedIds.length > 0;

  const selectedBase = buildSearchConditions({
    selectedStatuses,
    selectedPageTypes,
    selectedCustomizations,
    searchValue,
    advancedConditions,
  });

  const candidatesBase = buildSearchConditions({
    selectedStatuses,
    selectedPageTypes,
    selectedCustomizations,
    searchValue,
    advancedConditions,
    forceCompleted: true,
  });

  const selectedConditions = {
    ...selectedBase,
    ...(hasSelection ? { id_in: selectedIds } : {}),
  };

  // Candidates must be pages that are missing at least one of the chosen
  // content types, so at least one variant can actually be generated. The
  // backend exposes `without_any_customization_kind` (OR semantics: "missing
  // any of these kinds") for exactly this case; it coexists with any
  // `without_customization_kind` the page-table filter already set (e.g.
  // "No AI Content"), which uses AND semantics.
  const candidatesConditions = {
    ...candidatesBase,
    ...(hasSelection ? { id_in: selectedIds } : {}),
    ...(watchedVariants.length > 0
      ? {
          without_any_customization_kind: watchedVariants.map(
            variantToCustomizationKind,
          ),
        }
      : {}),
  };

  const {
    data: selectedCountData,
    isLoading: isSelectedCountLoading,
    isFetching: isSelectedCountFetching,
  } = useQuery({
    queryKey: queryKeys.generateCustomizationsSelectedCount(siteId, {
      searchValue,
      selectedStatuses,
      selectedPageTypes,
      selectedCustomizations,
      advancedConditions,
      selectedIds,
    }),
    queryFn: async () => {
      const response = await apiClient.sites.pagesDetail(siteId, {
        q: selectedConditions,
        "page[size]": 1,
        "page[number]": 1,
      });

      return {
        totalCount: parsePaginationHeaders(response.headers).totalCount,
      };
    },
    placeholderData: keepPreviousData,
  });

  const {
    data: candidatesCountData,
    isLoading: isCandidatesCountLoading,
    isFetching: isCandidatesCountFetching,
  } = useQuery({
    queryKey: queryKeys.generateCustomizationsCount(siteId, {
      searchValue,
      selectedStatuses,
      selectedPageTypes,
      selectedCustomizations,
      advancedConditions,
      selectedIds,
      variants: watchedVariants,
    }),
    queryFn: async () => {
      const response = await apiClient.sites.pagesDetail(siteId, {
        q: candidatesConditions,
        "page[size]": 1,
        "page[number]": 1,
      });

      return {
        totalCount: parsePaginationHeaders(response.headers).totalCount,
      };
    },
    // Keep the previous count on screen while a new one is being fetched
    // (the query key changes on every variant/filter toggle). Without this
    // the number would flash back through undefined → 0 and the summary
    // line would jump. `useStagedBoolean` below drives the inline spinner.
    placeholderData: keepPreviousData,
  });

  const selectedCount = selectedCountData?.totalCount ?? 0;
  const candidatesCount = candidatesCountData?.totalCount ?? 0;
  const isCountLoading = isSelectedCountLoading || isCandidatesCountLoading;
  const isCountFetching = isSelectedCountFetching || isCandidatesCountFetching;

  // Only show the refresh spinner when a refetch (not the initial load)
  // takes longer than 500ms, and keep it visible for at least 1s so it
  // doesn't flicker on quick responses.
  const showRefetchSpinner = useStagedBoolean(
    isCountFetching && !isCountLoading,
    500,
    1000,
  );

  useEffect(() => {
    // Disable Generate during the initial load and while the staged refetch
    // spinner is visible — toggling a content-type pill refires the candidates
    // query, and submitting against a stale count could pick up pages that no
    // longer match. Reusing the staged flag avoids a disabled→enabled flicker
    // on fast responses.
    onValidityChange(
      watchedVariants.length > 0 &&
        candidatesCount > 0 &&
        !isCountLoading &&
        !showRefetchSpinner,
    );
  }, [
    watchedVariants,
    candidatesCount,
    isCountLoading,
    showRefetchSpinner,
    onValidityChange,
  ]);

  const { mutate: createAction } = useMutation({
    mutationFn: async (attrs: SiteCustomizationActionAttributes) => {
      const { data, error } = await tryCatch(
        apiClient.sites.siteCustomizationActionsCreate(siteId, attrs),
      );

      if (error) {
        enqueueSnackbar(messageFromError(error), { variant: "error" });

        return;
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.siteCustomizationActions(siteId),
      });
      enqueueSnackbar("Generation started", { variant: "success" });
      onClose();

      return data;
    },
  });

  const toggleVariant = (variant: Variant) => {
    const next = watchedVariants.includes(variant)
      ? watchedVariants.filter((v) => v !== variant)
      : [...watchedVariants, variant];

    setValue("variants", next, { shouldValidate: true });
  };

  const onSubmit = (data: FormValues) => {
    createAction({
      mode: "manual",
      variants: data.variants,
      conditions: candidatesConditions,
    });
  };

  const showBothCounts = selectedCount !== candidatesCount;

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.inner}>
        <Controller
          name="variants"
          control={control}
          rules={{
            validate: (v) => v.length > 0 || "Select at least one variant",
          }}
          render={() => (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>Select content types</div>
              <div className={styles.variantsGroup}>
                {VARIANT_OPTIONS.map((opt) => (
                  <TogglePill
                    key={opt.value}
                    label={opt.label}
                    checked={watchedVariants.includes(opt.value)}
                    onChange={() => toggleVariant(opt.value)}
                  />
                ))}
              </div>
            </div>
          )}
        />

        <div className={styles.counts}>
          {isCountLoading ? (
            <span className={styles.summaryMuted}>
              Calculating matching pages…
            </span>
          ) : (
            <>
              <div className={styles.countRow}>
                <span className={styles.countLabel}>Selected:</span>
                <strong>{selectedCount}</strong>
                {!showBothCounts && showRefetchSpinner && (
                  <Spinner size="0.8rem" />
                )}
              </div>
              {showBothCounts && (
                <div className={styles.countRow}>
                  <span className={styles.countLabel}>Candidates:</span>
                  <strong>{candidatesCount}</strong>
                  {showRefetchSpinner && <Spinner size="0.8rem" />}
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.footnote}>
          * Only completed pages without existing AI Content
        </div>
      </div>
    </form>
  );
};
